import React, { useEffect, createContext, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import posthog from 'posthog-js';

// PostHog configuration
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY || process.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || process.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';

// Initialize PostHog
let isInitialized = false;

export function initPostHog() {
  if (isInitialized || !POSTHOG_KEY) {
    if (!POSTHOG_KEY) {
      console.warn('[PostHog] No API key found. Analytics disabled.');
    }
    return;
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: 'identified_only',
    capture_pageview: false, // We'll handle this manually with React Router
    capture_pageleave: true,
    autocapture: false, // Disabled to reduce event volume
    persistence: 'localStorage+cookie',
    loaded: (posthog) => {
      // Enable debug mode in development
      if (import.meta.env.DEV) {
        posthog.debug();
      }
    },
  });

  isInitialized = true;
}

// PostHog context for React components
interface PostHogContextType {
  isInitialized: boolean;
}

const PostHogContext = createContext<PostHogContextType>({ isInitialized: false });

export function usePostHog() {
  return useContext(PostHogContext);
}

// Page view tracker component
function PageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    if (POSTHOG_KEY) {
      posthog.capture('$pageview', {
        $current_url: window.location.href,
        path: location.pathname,
      });
    }
  }, [location.pathname]);

  return null;
}

// PostHog Provider component
interface PostHogProviderProps {
  children: React.ReactNode;
}

export function PostHogProvider({ children }: PostHogProviderProps) {
  useEffect(() => {
    initPostHog();
  }, []);

  return (
    <PostHogContext.Provider value={{ isInitialized: !!POSTHOG_KEY }}>
      <PageViewTracker />
      {children}
    </PostHogContext.Provider>
  );
}

// ==================== Analytics Event Functions ====================

/**
 * Identify a user after authentication
 */
export function identifyUser(userId: string, properties?: Record<string, unknown>) {
  if (!POSTHOG_KEY) return;
  
  posthog.identify(userId, properties);
}

/**
 * Reset user identity on logout
 */
export function resetUser() {
  if (!POSTHOG_KEY) return;
  
  posthog.reset();
}

/**
 * Track a custom event
 */
export function trackEvent(eventName: string, properties?: Record<string, unknown>) {
  if (!POSTHOG_KEY) return;
  
  posthog.capture(eventName, properties);
}

// ==================== Pre-defined Event Tracking Functions ====================

// --- Authentication Events ---

export function trackSignUp(method: string) {
  trackEvent('user_signed_up', { method });
}

export function trackSignIn(method: string) {
  trackEvent('user_signed_in', { method });
}

export function trackSignOut() {
  trackEvent('user_signed_out');
  resetUser();
}

// --- Diagram Events ---

export function trackDiagramCreated(diagramId: string, source: 'manual' | 'ai' | 'import' | 'duplicate') {
  trackEvent('diagram_created', { diagram_id: diagramId, source });
}

export function trackDiagramSaved(diagramId: string) {
  trackEvent('diagram_saved', { diagram_id: diagramId });
}

export function trackDiagramDeleted(diagramId: string) {
  trackEvent('diagram_deleted', { diagram_id: diagramId });
}

export function trackDiagramExported(format: 'png' | 'svg' | 'json') {
  trackEvent('diagram_exported', { format });
}

export function trackDiagramImported(nodeCount: number, edgeCount: number) {
  trackEvent('diagram_imported', { node_count: nodeCount, edge_count: edgeCount });
}

// --- AI Generation Events ---

export function trackAIGenerationStarted(promptLength: number) {
  trackEvent('ai_generation_started', { prompt_length: promptLength });
}

export function trackAIGenerationCompleted(nodeCount: number, edgeCount: number) {
  trackEvent('ai_generation_completed', { node_count: nodeCount, edge_count: edgeCount });
}

export function trackAIGenerationFailed(error: string) {
  trackEvent('ai_generation_failed', { error });
}

// --- Editor Events ---

export function trackAutoLayoutUsed(direction: 'TB' | 'LR') {
  trackEvent('auto_layout_used', { direction });
}

// --- Search Events ---

export function trackSearchUsed(query: string, resultCount: number) {
  trackEvent('search_used', { query_length: query.length, result_count: resultCount });
}

// Export posthog instance for advanced usage
export { posthog };

